import { unionRects, type BoardObject } from '@miroom/shared';

/**
 * Lightweight SVG preview of board objects (template gallery, thumbnails fallback).
 * Draws simplified shapes only; text is omitted except for large titles.
 */
export function MiniBoard({
  objects,
  width = 240,
  height = 150,
}: {
  objects: BoardObject[];
  width?: number;
  height?: number;
}) {
  const drawable = objects.filter((o) => o.type !== 'connector' && o.w > 0 && o.h > 0);
  const bounds = unionRects(drawable.map((o) => ({ x: o.x, y: o.y, w: o.w, h: o.h })));
  if (!bounds) {
    return (
      <svg width="100%" height="100%" viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
        <defs>
          <pattern id="dots" width="12" height="12" patternUnits="userSpaceOnUse">
            <circle cx="1.5" cy="1.5" r="1.2" fill="var(--canvas-dot)" />
          </pattern>
        </defs>
        <rect width={width} height={height} fill="url(#dots)" />
        <rect
          x={width / 2 - 18}
          y={height / 2 - 18}
          width="36"
          height="36"
          rx="3"
          fill="#fff9b1"
          transform={`rotate(-5 ${width / 2} ${height / 2})`}
        />
      </svg>
    );
  }
  const pad = Math.max(bounds.w, bounds.h) * 0.08;
  const vb = `${bounds.x - pad} ${bounds.y - pad} ${bounds.w + pad * 2} ${bounds.h + pad * 2}`;
  const byId = new Map(objects.map((o) => [o.id, o]));
  return (
    <svg
      width="100%"
      height="100%"
      viewBox={vb}
      preserveAspectRatio="xMidYMid meet"
      aria-hidden="true"
    >
      {objects.map((o) => {
        switch (o.type) {
          case 'frame':
            return (
              <rect
                key={o.id}
                x={o.x}
                y={o.y}
                width={o.w}
                height={o.h}
                rx={8}
                fill={o.fill}
                stroke="#cbd5e1"
                strokeWidth={4}
              />
            );
          case 'sticky':
            return (
              <rect key={o.id} x={o.x} y={o.y} width={o.w} height={o.h} rx={4} fill={o.color} />
            );
          case 'card':
            return (
              <g key={o.id}>
                <rect
                  x={o.x}
                  y={o.y}
                  width={o.w}
                  height={o.h}
                  rx={8}
                  fill="#fff"
                  stroke="#e2e8f0"
                  strokeWidth={4}
                />
                <rect x={o.x} y={o.y} width={10} height={o.h} fill={o.color} />
              </g>
            );
          case 'shape':
            return o.shape === 'ellipse' ? (
              <ellipse
                key={o.id}
                cx={o.x + o.w / 2}
                cy={o.y + o.h / 2}
                rx={o.w / 2}
                ry={o.h / 2}
                fill={o.fill}
                stroke={o.stroke}
                strokeWidth={4}
              />
            ) : o.shape === 'diamond' ? (
              <polygon
                key={o.id}
                points={`${o.x + o.w / 2},${o.y} ${o.x + o.w},${o.y + o.h / 2} ${o.x + o.w / 2},${o.y + o.h} ${o.x},${o.y + o.h / 2}`}
                fill={o.fill}
                stroke={o.stroke}
                strokeWidth={4}
              />
            ) : (
              <rect
                key={o.id}
                x={o.x}
                y={o.y}
                width={o.w}
                height={o.h}
                rx={o.shape === 'roundRect' ? 16 : 2}
                fill={o.fill}
                stroke={o.stroke}
                strokeWidth={4}
              />
            );
          case 'mindmap': {
            const parent = o.parentNode ? byId.get(o.parentNode) : null;
            return (
              <g key={o.id}>
                {parent && (
                  <line
                    x1={parent.x + parent.w / 2}
                    y1={parent.y + parent.h / 2}
                    x2={o.x + o.w / 2}
                    y2={o.y + o.h / 2}
                    stroke={o.color}
                    strokeWidth={4}
                  />
                )}
                <rect
                  x={o.x}
                  y={o.y}
                  width={o.w}
                  height={o.h}
                  rx={o.h / 2}
                  fill={parent ? '#fff' : o.color}
                  stroke={o.color}
                  strokeWidth={4}
                />
              </g>
            );
          }
          case 'text':
            return (
              <rect
                key={o.id}
                x={o.x}
                y={o.y + o.h * 0.25}
                width={Math.min(o.w, o.text.length * o.style.fontSize * 0.55)}
                height={o.h * 0.5}
                rx={4}
                fill="var(--text-3)"
                opacity={0.5}
              />
            );
          case 'image':
            return <rect key={o.id} x={o.x} y={o.y} width={o.w} height={o.h} fill="#cbd5e1" />;
          case 'table':
            return (
              <rect
                key={o.id}
                x={o.x}
                y={o.y}
                width={o.w}
                height={o.h}
                fill="#fff"
                stroke={o.stroke}
                strokeWidth={3}
              />
            );
          case 'emoji':
            return (
              <circle key={o.id} cx={o.x + o.w / 2} cy={o.y + o.h / 2} r={o.w / 2} fill="#fde047" />
            );
          case 'drawing':
            return null;
          default:
            return null;
        }
      })}
      {objects.map((o) => {
        if (o.type !== 'connector') return null;
        const a = o.from.objectId ? byId.get(o.from.objectId) : null;
        const b = o.to.objectId ? byId.get(o.to.objectId) : null;
        if (!a || !b) return null;
        return (
          <line
            key={o.id}
            x1={a.x + a.w / 2}
            y1={a.y + a.h / 2}
            x2={b.x + b.w / 2}
            y2={b.y + b.h / 2}
            stroke={o.stroke}
            strokeWidth={4}
            opacity={0.6}
          />
        );
      })}
    </svg>
  );
}
